import React, { useMemo, useState } from 'react';
import { APITypes } from '../../../../primary-record-types';
import { FastField } from 'formik';
import { getIntl } from '../../../../utils/intl';
import { required } from '../../../../utils/validators';
import Actions from './Actions';
import AttachmentPicker, { AttachmentType } from './AttachmentPicker';
import AttachmentList from './AttachmentList';
import { NoteUpsert } from '../../../../primary-record-types/apiTypes/notesService';
import { noteFileToNotModified } from '../utils/noteFileToNoteModified';
import { NoteDestination } from '../utils/types';
import { handleFormikSubmit } from '../../../../utils/handleFormikSubmit';
import { useLoadedUser } from '../../../../hooks/api/useUser';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { Form } from '../../../v2BaseComponents/Form';
import Stack, { ContentStack, contentStackSpacing } from '../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../utils/spacing';
import DetailsLayout from '../../../v2BaseComponents/DetailsLayout';
import MaxWidthRestrictedContent from '../../../v2BaseComponents/MaxWidthRestrictedContent';
import { View } from 'react-native';
import { ChipSet } from '../../../v2BaseComponents/Chip';
import { noteCategoryTagMessages } from '../../../../utils/sharedMessages';
import { AudioRecordingContainer, Recorder } from '../../AudioRecording';
import useRecordEvent from '../../../../hooks/useRecordEvent';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import {
    useHasReachedAudioFileUploadLimit,
    useHasReachedFileUploadLimit,
} from '../../../../hooks/api/useMedicalProfileUsageMetric';
import { getDefaultNoteFromNoteDestination } from '../../../../hooks/api/useNotes';
import { buildNoteSaveInstrumentation, reportNoteSaveToBugsnag } from '../../../../utils/noteSaveInstrumentation';
import { IsEditingNoteContentProvider } from '../../../v2BaseComponents/Inputs/NoteContentInput';

const intl = getIntl('note-editor', {
    title: 'Title',
    note: 'Note',
    save: 'Save',
    cancel: 'Cancel',
    attachment: 'Attachments ({count})',
    newRecording: 'New Recording {date}',
});

export type SubmitArgs = {
    note: APITypes.notesService.NoteUpsert;
    drySubmit: boolean;
};

type ProfileLimitProps = {
    canAddFiles: boolean;
    canAddAudioRecording: boolean;
};

export type NoteEditorOwnProps = {
    onSubmit: (args: SubmitArgs) => void;
    onCancel: () => void;
    isDisabled?: boolean;
    note?: APITypes.notesService.NoteDetails;
    noteDestination: NoteDestination;
    defaultTab?: 'note' | 'attachment';
    defaultOpenAttachmentType?: AttachmentType;
    isLoading?: boolean;
    disableRecording?: boolean;
    /** Identifies which editor host this is, for misroute instrumentation (Bugsnag/analytics). */
    source?: string;
    /**
     * A stale create-latch note that useEditNote detected belonged to a previous destination and
     * ignored (so this save creates a fresh note here instead of overwriting it). Present === a
     * cross-profile/visit misroute was detected and prevented; drives the misroute instrumentation.
     */
    ignoredStaleNote?: APITypes.notesService.NoteDetails;
};

export type NoteEditorProps = NoteEditorOwnProps;

const getMedicalProfileId = (noteDestination: NoteDestination): string | null => {
    if ('medicalProfileId' in noteDestination) {
        return noteDestination.medicalProfileId;
    } else {
        return null;
    }
};

const NoteEditor: React.FC<NoteEditorProps & { onRefreshState: () => void } & ProfileLimitProps> = ({
    note,
    onSubmit,
    onCancel,
    isDisabled,
    noteDestination,
    defaultOpenAttachmentType,
    onRefreshState,
    isLoading,
    canAddFiles,
    canAddAudioRecording,
    disableRecording = false,
    source,
    ignoredStaleNote,
}) => {
    const { user } = useLoadedUser({});
    const [focusedAttachmentPickerType, setFocusedAttachmentPickerType] = React.useState<AttachmentType | null>(
        defaultOpenAttachmentType || null,
    );
    const [isToolbarVisible, setIsToolbarVisible] = React.useState(false);
    const wasDrySubmitRef = React.useRef<boolean>(false);
    const record = useRecordEvent();

    const { entityType } = noteDestination;
    const initialValue = useMemo<NoteUpsert>(() => {
        if (!note) {
            return getDefaultNoteFromNoteDestination(noteDestination, user.userId);
        } else {
            // A note loaded here belongs to this destination (useEditNote ignores stale cross-destination
            // notes before they reach the editor), so edit it in place using its own entity fields.
            return {
                title: note.title,
                content: note.htmlContent,
                tags: note.tags,
                files: note.files.map(noteFileToNotModified),
                images: note.images.map(noteFileToNotModified),
                entityId: note.entityId,
                entityType: note.entityType,
                medicalProfileId: note.medicalProfileId,
                organizationId: note.organizationId,
            };
        }
    }, [note, noteDestination, user.userId]);

    return (
        <AnimateLoadedContent isLoading={!!isLoading}>
            {() => (
                <IsEditingNoteContentProvider>
                    {({ setEditor, setToolbarItems }) => (
                        <MaxWidthRestrictedContent
                            style={{ flex: 1, height: '100%' }}
                            contentContainerStyle={{ alignSelf: 'stretch' }}
                        >
                            <AudioRecordingContainer style={{ width: '100%', height: '100%' }}>
                                <Form
                                    noScroll
                                    actionContainerSubmit
                                    saveText={intl('save')}
                                    onCancel={() => {
                                        onCancel();
                                        onRefreshState();
                                    }}
                                    cancelText={intl('cancel')}
                                    cancelButtonVariant="secondary"
                                    initialValues={initialValue}
                                    onSubmitPress={(onFormSubmit) => {
                                        onFormSubmit();
                                    }}
                                    onSubmit={(values, helpers) => {
                                        onSubmit({
                                            note: values,
                                            drySubmit: wasDrySubmitRef.current,
                                        });
                                        wasDrySubmitRef.current = false;
                                        handleFormikSubmit(values, helpers);
                                        // Best-effort instrumentation — must never block or break the save above.
                                        try {
                                            const noteSaveInstrumentation = buildNoteSaveInstrumentation(
                                                noteDestination,
                                                ignoredStaleNote,
                                                user.userId,
                                                source,
                                            );
                                            record('addNote', noteSaveInstrumentation.attributes);
                                            reportNoteSaveToBugsnag(noteSaveInstrumentation, {
                                                noteId: note?.noteId,
                                                source,
                                            });
                                        } catch {
                                            // never let instrumentation interrupt the save
                                        }
                                    }}
                                    fillContainer
                                    isDisabled={isDisabled}
                                    contentContainerStyle={{ paddingTop: 0, paddingBottom: 0 }}
                                >
                                    {({ values, setFieldValue, handleSubmit }) => (
                                        <>
                                            <AttachmentPicker
                                                attachmentType="image"
                                                isOpen={focusedAttachmentPickerType === 'image'}
                                                onCancel={() => setFocusedAttachmentPickerType(null)}
                                                onPick={(attachment) => {
                                                    setFocusedAttachmentPickerType(null);
                                                    setFieldValue('images', [...values.images, attachment]);
                                                }}
                                            />
                                            <AttachmentPicker
                                                attachmentType="file"
                                                isOpen={focusedAttachmentPickerType === 'file'}
                                                onCancel={() => setFocusedAttachmentPickerType(null)}
                                                onPick={(attachment) => {
                                                    setFocusedAttachmentPickerType(null);
                                                    setFieldValue('files', [...values.files, attachment]);
                                                }}
                                            />
                                            <ContentStack>
                                                <View
                                                    style={{
                                                        height: 20,
                                                        marginBottom: 5,
                                                        width: '100%',
                                                    }}
                                                >
                                                    <FastField
                                                        component={FormikInputs.NoteTitleInput}
                                                        name="title"
                                                        isRequired
                                                        isDisabled={isDisabled}
                                                        validate={required}
                                                        placeholder={intl('title')}
                                                    />
                                                </View>

                                                <DetailsLayout testID="note-editor-details-layout">
                                                    <FastField
                                                        component={FormikInputs.NoteContentInput}
                                                        name="content"
                                                        isDisabled={isDisabled}
                                                        placeholder={intl('note')}
                                                        size={'lg'}
                                                        multiline
                                                        transparent
                                                        showToolbar={isToolbarVisible}
                                                        setEditor={setEditor}
                                                        setToolbarItems={setToolbarItems}
                                                    />
                                                    {!isToolbarVisible && (
                                                        <AttachmentList
                                                            images={values.images}
                                                            files={values.files}
                                                            onUpdateImages={(images) => setFieldValue('images', images)}
                                                            onUpdateFiles={(files) => setFieldValue('files', files)}
                                                            isDisabled={!!isDisabled}
                                                            noteId={note?.noteId}
                                                        />
                                                    )}
                                                    {disableRecording ? null : (
                                                        <Recorder
                                                            onRecordingCreated={(recording) => {
                                                                async function updateFields() {
                                                                    const newValue: APITypes.notesService.NoteFileUpdate[] =
                                                                        [
                                                                            ...values.files,
                                                                            {
                                                                                type: 'create',
                                                                                tempFileId: recording.tempFileId,
                                                                                description: recording.description,
                                                                                contentType: recording.contentType,
                                                                                name: recording.name,
                                                                            },
                                                                        ];

                                                                    await setFieldValue('files', newValue);
                                                                    if (!values.title) {
                                                                        await setFieldValue(
                                                                            'title',
                                                                            intl('newRecording', {
                                                                                date: new Date().toLocaleString(),
                                                                            }),
                                                                        );
                                                                    }
                                                                }

                                                                wasDrySubmitRef.current = true;
                                                                updateFields().then(() => {
                                                                    setTimeout(handleSubmit, 1);
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                    <ChipSet
                                                        style={{ marginTop: getSpacing(2.5) }}
                                                        chips={values.tags.map((_) => ({
                                                            title: noteCategoryTagMessages(_),
                                                            highlight: true,
                                                            testID: `note-tag-${_}`,
                                                            onDelete: () => {
                                                                setFieldValue(
                                                                    'tags',
                                                                    values.tags.filter((tag) => tag !== _),
                                                                );
                                                            },
                                                        }))}
                                                    />
                                                </DetailsLayout>
                                                {
                                                    <Stack
                                                        vertical
                                                        fullWidth
                                                        space={contentStackSpacing}
                                                        style={{
                                                            marginTop: getSpacing(8),
                                                        }}
                                                    >
                                                        <Actions
                                                            isDisabled={!!isDisabled}
                                                            onPromptAddImage={() =>
                                                                setFocusedAttachmentPickerType('image')
                                                            }
                                                            onPromptAddFile={() =>
                                                                setFocusedAttachmentPickerType('file')
                                                            }
                                                            tagValue={values.tags}
                                                            onTagChange={(value) => {
                                                                setFieldValue('tags', value);
                                                            }}
                                                            defaultOpenAttachmentType={defaultOpenAttachmentType}
                                                            isOrganizationClientRecordNote={
                                                                entityType === 'organization-client-record'
                                                            }
                                                            isAddFilesDisabled={!canAddFiles}
                                                            isAddRecordingsDisabled={!canAddAudioRecording}
                                                            medicalProfileId={getMedicalProfileId(noteDestination)}
                                                            disableRecording={disableRecording}
                                                            isToolbarVisible={isToolbarVisible}
                                                            onToggleToolbar={() =>
                                                                setIsToolbarVisible(!isToolbarVisible)
                                                            }
                                                        />
                                                    </Stack>
                                                }
                                            </ContentStack>
                                        </>
                                    )}
                                </Form>
                            </AudioRecordingContainer>
                        </MaxWidthRestrictedContent>
                    )}
                </IsEditingNoteContentProvider>
            )}
        </AnimateLoadedContent>
    );
};

const WithProfileLimitProvider: React.FC<
    NoteEditorProps & { medicalProfileId: string; onRefreshState: () => void }
> = ({ medicalProfileId, ...toPass }) => {
    const hasReachedFileUploadLimit = useHasReachedFileUploadLimit(medicalProfileId);
    const hasReachedAudioRecordingLimit = useHasReachedAudioFileUploadLimit(medicalProfileId);
    return (
        <NoteEditor
            {...toPass}
            canAddAudioRecording={!hasReachedAudioRecordingLimit}
            canAddFiles={!hasReachedFileUploadLimit}
        />
    );
};

const RemountedNoteEditor: React.FC<NoteEditorProps> = (props) => {
    const [remountKey, setRemountKey] = useState(0);
    const medicalProfileId =
        'medicalProfileId' in props.noteDestination ? props.noteDestination.medicalProfileId : null;

    if (medicalProfileId) {
        return (
            <WithProfileLimitProvider
                key={`${remountKey}-${JSON.stringify(props.noteDestination)}-${JSON.stringify(props.note)}`}
                {...props}
                medicalProfileId={medicalProfileId}
                onRefreshState={() => {
                    setRemountKey(remountKey + 1);
                }}
            />
        );
    } else {
        return (
            <NoteEditor
                key={`${remountKey}-${JSON.stringify(props.noteDestination)}-${JSON.stringify(props.note)}`}
                {...props}
                onRefreshState={() => {
                    setRemountKey(remountKey + 1);
                }}
                canAddAudioRecording
                canAddFiles
            />
        );
    }
};

export default RemountedNoteEditor;
